import { useEffect, useRef, useState } from 'react';
import './campGuestSelector.css';

/*
 * Guest picker for the camp/villa search bar only.
 *
 * This is a NEW, separate component - it does not touch or replace
 * components/guestSelector/GuestSelector.jsx, which is still used by
 * the other search tabs (trip/backpack, activity, guide) and is left
 * exactly as it was.
 *
 * Unlike the old GuestSelector, there is no "Rooms" concept here -
 * per the redesign request, this follows the MakeMyTrip/Airbnb pattern
 * of a single Adults/Children stepper pair. `value`/`onChange` use the
 * same {adults, children, pets} shape the rest of the camp search bar and
 * the URL (?adults=2&children=1&pets=1) use; pets > 0 shows only stays
 * that allow pets (the server filters them).
 */

// pets: same limit as the stay booking tab and the server
const LIMITS = {
    adults: { min: 1, max: 10 },
    children: { min: 0, max: 6 },
    pets: { min: 0, max: 5 }
};

const ROWS = [
    { key: 'adults', title: 'Adults', subtitle: 'Ages 13 or above' },
    { key: 'children', title: 'Children', subtitle: 'Ages 0 - 12' },
    { key: 'pets', title: 'Pets', subtitle: 'Shows only pet-friendly stays' }
];

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const CampGuestSelector = ({ value, onChange, open = false, onApply }) => {
    const [isOpen, setIsOpen] = useState(false);
    const popupRef = useRef(null);

    const counts = {
        adults: value?.adults ?? 1,
        children: value?.children ?? 0,
        pets: value?.pets ?? 0
    };

    useEffect(() => {
        if (open) {
            setIsOpen(true);
        }
    }, [open]);

    useEffect(() => {
        const handleClickOutsidePopup = (event) => {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutsidePopup);
        return () => {
            document.removeEventListener('mousedown', handleClickOutsidePopup);
        };
    }, []);

    const update = (key, delta) => {
        const { min, max } = LIMITS[key];
        const next = Math.min(max, Math.max(min, counts[key] + delta));
        if (next !== counts[key]) {
            onChange?.({ ...counts, [key]: next });
        }
    };

    const summaryLabel = [
        plural(counts.adults, 'Adult', 'Adults'),
        counts.children > 0 && plural(counts.children, 'Child', 'Children'),
        counts.pets > 0 && plural(counts.pets, 'Pet', 'Pets')
    ]
        .filter(Boolean)
        .join(', ');

    return (
        <div ref={popupRef} className='campGuest-container'>
            <button
                type='button'
                className='campGuest-trigger'
                onClick={() => setIsOpen((prev) => !prev)}
            >
                {summaryLabel}
            </button>

            {isOpen && (
                <div className='campGuest-popup'>
                    {ROWS.map((row) => (
                        <div key={row.key} className='campGuest-row'>
                            <div className='campGuest-rowText'>
                                <span className='campGuest-rowTitle'>{row.title}</span>
                                <span className='campGuest-rowSubtitle'>{row.subtitle}</span>
                            </div>

                            <div className='campGuest-stepper'>
                                <button
                                    type='button'
                                    className='campGuest-stepperBtn'
                                    disabled={counts[row.key] <= LIMITS[row.key].min}
                                    onClick={() => update(row.key, -1)}
                                    aria-label={`Decrease ${row.key}`}
                                >
                                    −
                                </button>
                                <span className='campGuest-stepperValue'>{counts[row.key]}</span>
                                <button
                                    type='button'
                                    className='campGuest-stepperBtn'
                                    disabled={counts[row.key] >= LIMITS[row.key].max}
                                    onClick={() => update(row.key, 1)}
                                    aria-label={`Increase ${row.key}`}
                                >
                                    +
                                </button>
                            </div>
                        </div>
                    ))}

                    <button
                        type='button'
                        className='campGuest-applyBtn'
                        onClick={() => {
                            setIsOpen(false);
                            onApply?.();
                        }}
                    >
                        Apply
                    </button>
                </div>
            )}
        </div>
    );
};

export default CampGuestSelector;
